/***********************************************************
 Vuex User Store
 --------------------------
 Store to use for detail pages of a user.
 ************************************************************/
import {defineStore} from 'pinia'
import {ModelType} from "@spoferan/spoferan-ts-core";
import type {User} from "@spoferan/spoferan-ts-core";

type UserAuthPermissions = {
    is_initialized: boolean, // Whether the permissions were loaded
    is_user: boolean, // Whether the auth user is the user
    is_parent: boolean,
    can_edit: boolean
};

const defaultAuthPermissions = {
    is_initialized: false,
    is_user: false,
    is_parent: false,
    can_edit: false,
} as UserAuthPermissions

export const useUserStore = defineStore('user', {
    state: () => ({
        /**
         * The current user details to show.
         */
        user: {} as User,

        /**
         * The permissions / relations of the currently authenticated user on the user.
         */
        authPermissions: {...defaultAuthPermissions} as UserAuthPermissions,
    }),

    getters: {
        colorStyle: (state) => {
            const config = useRuntimeConfig();
            return state.user.color ?? config.public.colors.primary;
        }
    },

    actions: {
        async refreshAuthPermissions() {
            const {$auth, $apiFetch} = useNuxtApp();

            // We know the permissions on client side for guests, so we can skip the request
            if (!$auth.loggedIn) {
                this.authPermissions = {...defaultAuthPermissions, is_initialized: true};
                return;
            }

            const route = useRoute();

            const {data} = await $apiFetch('/v1/auth/permissions', {
                params: {
                    model_type: ModelType.User,
                    model_id: route.params.user, // Use route param to refresh auth permissions asap for page redirects
                }
            });
            this.authPermissions = {...data, is_initialized: true};
        },

        /**
         * Reloads the data of the currently logged user, if the logged user is the same es the store user.
         */
        async reloadLoggedUserDataIfSameUser() {
            const {$auth} = useNuxtApp();

            if (this.user.id === $auth.user.id) {
                await $auth.fetchUser();
            }
        },

        replaceUserData(data: { [key: string]: any }) {
            this.$patch((state) => {
                Object.keys(data).forEach(key => {
                    state.user[key] = data[key];
                });
            })
        },

        /**
         * Updates the data of the store user with the specified data.
         */
        async updateStoreUser(data: { [key: string]: any }) {
            this.replaceUserData(data);
            await this.reloadLoggedUserDataIfSameUser()
        },

        buildPageTitle(title: string): string {
            return title === this.user.display_name ? title : `${title} | ${this.user.display_name}`
        }
    }
});